"use client";

import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, MapPin } from "lucide-react";
import { geocoding } from "@maptiler/client";
import { parseLatLngToCenter } from "@/lib/coordinates";

const API_KEY = process.env.NEXT_PUBLIC_MAPTILER_KEY ?? "";

interface GeoResult {
  id: string;
  place_name: string;
  center: [number, number];
  bbox?: [number, number, number, number];
}

interface MapSearchProps {
  onResult: (center: [number, number], bbox?: [number, number, number, number]) => void;
}

export default function MapSearch({ onResult }: MapSearchProps) {
  const [expanded, setExpanded] = useState(false);
  const [query, setQuery]       = useState("");
  const [results, setResults]   = useState<GeoResult[]>([]);
  const [loading, setLoading]   = useState(false);
  const inputRef  = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Focus input when expanded
  useEffect(() => {
    if (expanded) inputRef.current?.focus();
  }, [expanded]);

  // Click-outside to collapse
  useEffect(() => {
    if (!expanded) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        collapse();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [expanded]);

  const collapse = () => {
    setExpanded(false);
    setQuery("");
    setResults([]);
  };

  const search = useCallback(async (q: string) => {
    const coords = parseLatLngToCenter(q);
    if (coords) {
      // Pin immediately, then look up the nearest place name so the box
      // shows *something* confirming what was found instead of going blank.
      onResult(coords);
      setLoading(true);
      let placeName = `${coords[1].toFixed(5)}, ${coords[0].toFixed(5)}`;
      try {
        const res = await geocoding.reverse(coords, { apiKey: API_KEY });
        const feature = res.features?.[0] as any;
        placeName = feature?.place_name ?? feature?.text ?? placeName;
      } catch { /* keep the raw-coordinate fallback label */ }
      setLoading(false);
      setResults([{ id: "coord-pin", place_name: placeName, center: coords }]);
      return;
    }
    if (q.trim().length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const res = await geocoding.forward(q, { apiKey: API_KEY, limit: 5 });
      setResults(
        (res.features ?? []).map((f: any) => ({
          id: f.id,
          place_name: f.place_name ?? f.text ?? "",
          center: f.center as [number, number],
          bbox: f.bbox,
        }))
      );
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [onResult]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), 300);
  };

  const handleSelect = (r: GeoResult) => {
    onResult(r.center, r.bbox);
    collapse();
  };

  return (
    <div ref={wrapperRef} className="relative">
      <motion.div
        animate={{ width: expanded ? 220 : 36 }}
        transition={{ type: "spring", stiffness: 340, damping: 28 }}
        className="flex items-center bg-canvas/40 backdrop-blur-2xl rounded-2xl overflow-hidden h-9 shadow-2xl"
      >
        {/* Search icon / button */}
        <button
          onClick={() => expanded ? collapse() : setExpanded(true)}
          className="w-9 h-9 shrink-0 flex items-center justify-center text-tone-400 hover:text-ink transition-colors"
        >
          {expanded ? <X className="w-3.5 h-3.5" /> : <Search className="w-3.5 h-3.5" />}
        </button>

        {/* Input — only rendered when expanded to avoid tab-focusable hidden input */}
        <AnimatePresence>
          {expanded && (
            <motion.input
              ref={inputRef}
              key="search-input"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              value={query}
              onChange={handleChange}
              onKeyDown={(e) => e.key === "Escape" && collapse()}
              placeholder="Search places…"
              className="flex-1 bg-transparent text-ink text-[11px] font-medium placeholder-tone-500 outline-none pr-3 min-w-0"
            />
          )}
        </AnimatePresence>
      </motion.div>

      {/* Results dropdown */}
      <AnimatePresence>
        {expanded && (results.length > 0 || loading) && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0,  scale: 1    }}
            exit={{   opacity: 0, y: -6, scale: 0.97 }}
            transition={{ type: "spring", stiffness: 400, damping: 28 }}
            className="absolute top-full left-0 mt-2 w-64 bg-canvas/70 backdrop-blur-2xl border border-ink/10 rounded-2xl p-1.5 shadow-2xl z-50"
          >
            {loading && (
              <p className="text-[10px] text-tone-500 px-3 py-2">Searching…</p>
            )}
            {!loading && results.map((r) => (
              <button
                key={r.id}
                onClick={() => handleSelect(r)}
                className="w-full flex items-start gap-2 px-3 py-2 rounded-xl text-left hover:bg-ink/10 transition-all"
              >
                <MapPin className="w-3 h-3 text-tone-500 shrink-0 mt-0.5" />
                <span className="text-[11px] text-tone-200 leading-snug line-clamp-2">{r.place_name}</span>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
